import React, { useEffect, useState } from 'react';
import { Box, SelectChangeEvent } from '@mui/material';
import BasicSelect from 'components/basic-select';
import { useTranslation } from 'react-i18next';

const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const weekdayOptions: { label: string; value: string }[] = weekdays.map((day) => ({
  label: day,
  value: day
}));

interface WeekdayRangePickerProps {
  startDay: string;
  endDay: string;
  required?: boolean;
  handleStartDay: (day: string) => void;
  handleEndDay: (day: string) => void;
  readOnly?: boolean;
  startHelperText?: string;
  endHelperText?: string;
}

export const WeekdayRangePicker: React.FC<WeekdayRangePickerProps> = ({
  startDay,
  endDay,
  handleStartDay,
  handleEndDay,
  startHelperText,
  endHelperText,
  readOnly = false,
  required = false
}) => {
  const { t } = useTranslation();
  const [startError, setStartError] = useState(false);
  const [endError, setEndError] = useState(false);

  useEffect(() => {
    if (required) {
      setStartError(startHelperText ? true : false);
      setEndError(endHelperText ? true : false);
    }
  }, [startHelperText, endHelperText]);

  const onStartDayChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;

    // If required and no value selected, show error and exit
    if (required && !value) {
      setStartError(true);
      return;
    }

    setStartError(false); // clear error
    handleStartDay(value);

    const startIndex = weekdays.indexOf(value);
    const endIndex = weekdays.indexOf(endDay);

    // If endDay is set and comes before startDay, reset endDay
    if (endDay && endIndex < startIndex) {
      handleEndDay('');
    }
  };

  const onEndDayChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;

    // Check if selection is required but value is empty
    if (required && !value) {
      setEndError(true);
      return;
    }

    // Clear error and set selected value
    setEndError(false);
    handleEndDay(value);
  };

  return (
    <Box display="flex" gap={2}>
      {/* Start Day */}
      <Box flex={1}>
        <BasicSelect
          labelText={t('organization.startDay')}
          value={startDay}
          onChange={onStartDayChange}
          readOnly={readOnly}
          menuItem={weekdayOptions}
          required
          fullWidth
          error={startError}
          helperText={startError ? startHelperText : ''}
          variant="contained"
        />
      </Box>

      {/* End Day */}
      <Box flex={1}>
        <BasicSelect
          labelText={t('organization.endDay')}
          value={endDay}
          onChange={onEndDayChange}
          readOnly={readOnly}
          menuItem={weekdayOptions}
          required
          fullWidth
          error={endError}
          helperText={endError ? endHelperText : ''}
          variant="contained"
        />
      </Box>
    </Box>
  );
};
